<template>
  <div class="clinic-quick-links"
    :class="{ 'show-desktop': props.showOnDesktop, 'colorful-icons': props.colorful, 'monochrome-icons': !props.colorful }">
    <div class="links-scroll">
      <button v-for="item in items" :key="item.route" class="quick-tile" @click="navigateTo(item)">
        <span v-if="item.icon && !props.colorful" class="quick-icon-mask" :style="maskStyle(item.icon)"></span>
        <img v-if="item.icon && props.colorful" :src="imgPath(item.icon)" alt="" class="quick-icon-fallback" />
        <div class="quick-label">{{ item.title }}</div>
      </button>
    </div>
  </div>
</template>

<script setup>
/* global defineProps */
import { ref, onMounted } from 'vue';
const props = defineProps({ showOnDesktop: { type: Boolean, default: false }, colorful: { type: Boolean, default: true } });
import router from '@/router';
import { getGlobalSidebar, sidebarItemsMap } from '@/data/menus.js';
import { usePermissionAccessStore } from '@/stores/common/permissionAccess';

const items = ref([]);

function getPermissionHasAccess(p) {
  return Boolean(p && (p.hasAccess ?? p.HasAccess ?? p.has_access));
}

function getPermissionActionName(p) {
  return p && (p.actionName ?? p.ActionName ?? p.action_name);
}

async function getPermissionsArray(permsStore) {
  const fromStateRaw = permsStore && permsStore.permissionAccessEntity;
  const fromState = Array.isArray(fromStateRaw) ? fromStateRaw : (fromStateRaw && Array.isArray(fromStateRaw.value) ? fromStateRaw.value : []);
  if (fromState.length) return fromState;

  const fetchedRaw = await (permsStore && permsStore.fetchPermissionAccess ? permsStore.fetchPermissionAccess() : undefined);
  const fetched = Array.isArray(fetchedRaw) ? fetchedRaw : (fetchedRaw && Array.isArray(fetchedRaw.value) ? fetchedRaw.value : []);
  if (fetched.length) return fetched;

  const fromStateAfterRaw = permsStore && permsStore.permissionAccessEntity;
  const fromStateAfter = Array.isArray(fromStateAfterRaw) ? fromStateAfterRaw : (fromStateAfterRaw && Array.isArray(fromStateAfterRaw.value) ? fromStateAfterRaw.value : []);
  return fromStateAfter;
}

onMounted(async () => {
  try {
    const groups = await getGlobalSidebar();
    // find the crafts group and then the clinic child
    const clinicChildren = (groups || []).flatMap(g => g.children || []).find(i => i.route === 'clinic');
    if (clinicChildren && Array.isArray(clinicChildren.children)) {
      const loadedChildren = clinicChildren.children.filter(i => i.route !== 'clinic-dashboard');
      // Default to what sidebar gave us; we may override with permission filtering below.
      items.value = loadedChildren;
      // If loadedChildren length equals static map length, it might not have been permission-filtered.
      const staticChildren = (sidebarItemsMap['clinic'] || []).filter(i => i.route !== 'clinic-dashboard');
      if (loadedChildren.length === staticChildren.length) {
        // Fallback: apply explicit permission-based filtering using permission store
        try {
          const permsStore = usePermissionAccessStore();
          const perms = await getPermissionsArray(permsStore);
          const allowed = new Set(
            (perms || [])
              .filter(p => p && getPermissionHasAccess(p) && getPermissionActionName(p))
              .map(p => String(getPermissionActionName(p)).trim().toLowerCase())
          );
          const tokens = new Set();
          allowed.forEach(an => an.split(/[^a-z0-9]+/i).forEach(t => { if (t) tokens.add(t); }));
          // Detect if there are granular clinic permissions (e.g., SinaPack.MainPage.Crafts.Clinic.X)
          const hasClinicGranular = (perms || []).some(p => p && getPermissionHasAccess(p) && String(getPermissionActionName(p) || '').trim().toLowerCase().startsWith('sinapack.mainpage.crafts.clinic.'));
          // If granular clinic permissions exist, require explicit actionName match (case-insensitive)
           const filtered = staticChildren.filter(s => {
            if (s.actionName && allowed.has(String(s.actionName).trim().toLowerCase())) return true;
            if (hasClinicGranular) {
              // granular permissions present but this item doesn't have explicit actionName allowed
              return false;
            }
            const routeTokens = String(s.route || '').toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);
            if (routeTokens.some(t => tokens.has(t))) return true;
            const titleTokens = String(s.title || '').toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);
            if (titleTokens.some(t => tokens.has(t))) return true;
            return false;
           });
          items.value = filtered;
          return;
        } catch (err) {
          // fallback to static children below
        }
      }
      return;
    }
  } catch (err) {
    // ignore and fall back to static map
  }
  // fallback: use static sidebarItemsMap (unfiltered)
  items.value = (sidebarItemsMap['clinic'] || []).filter(i => i.route !== 'clinic-dashboard');
});

const publicPath = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.BASE_URL) ||
  (typeof process !== 'undefined' && process.env && process.env.BASE_URL) ||
  '/';
function imgPath(icon) { return `${publicPath}img/${icon}`; }
function navigateTo(item) {
  if (!item || !item.route) return;
  if (item.route.includes('/')) { router.push(`/overview/${item.route}`); return; }
  if (item.route.startsWith('clinic-')) { router.push(`/overview/clinic/${item.route}`); return; }
  router.push(`/overview/${item.route}`);
}

function maskStyle(icon) {
  const url = imgPath(icon);
  return {
    backgroundColor: 'var(--text-color-primary)',
    WebkitMaskImage: `url(${url})`,
    maskImage: `url(${url})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    width: '32px',
    height: '32px',
    display: 'inline-block',
  };
}
</script>

<style scoped>
.clinic-quick-links {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-color)
}

.links-scroll {
  display: flex;
  gap: 8px;
  overflow: auto;
  padding-bottom: 2px
}

.quick-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--surface-color);
  min-width: 86px;
  border: 1px solid var(--border-color);
  color: var(--text-color-primary)
}

.quick-icon-fallback {
  width: 32px;
  height: 32px
}

.quick-icon-mask {
  display: none;
  width: 32px;
  height: 32px;
  background-color: var(--text-color-primary)
}

.quick-label {
  font-size: 12px;
  color: inherit
}

.clinic-quick-links .quick-icon-fallback {
  filter: none
}

/* If we're using monochrome icons (mask) make sure the masked background uses the right color in dark theme */
:deep(body.dark-theme) .clinic-quick-links.monochrome-icons .quick-icon-mask {
  background-color: var(--text-color-forthly) !important;
}

/* Respect the colorful prop. If colorful icons are enabled, show fallback images no matter mask support. */
.colorful-icons .quick-icon-fallback {
  display: inline-block !important
}

.colorful-icons .quick-icon-mask {
  display: none !important
}

/* If not colorful, use mask where possible (mask takes priority if supported) */
@supports (-webkit-mask-image: none) {
  .monochrome-icons .quick-icon-mask {
    display: inline-block
  }

  .monochrome-icons .quick-icon-fallback {
    display: none
  }
}

@supports (mask-image: none) {
  .monochrome-icons .quick-icon-mask {
    display: inline-block
  }

  .monochrome-icons .quick-icon-fallback {
    display: none
  }
}

/* hide on large screens, as sidebar already gives full access */
@media(min-width:769px) {
  .clinic-quick-links {
    display: none
  }
}

.clinic-quick-links.show-desktop {
  display: block !important
}

.quick-tile:hover {
  background: var(--hover-background-color);
  border-color: var(--border-color)
}
</style>
